import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Tag, Input, Select, Form } from 'antd';
// import { useSelector } from 'react-redux';
import { LeftOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty, debounce } from 'underscore';

import { AppDispatch, CombinedStates } from '../../core/types';
import Main from '../../components/layout/Main';
import { getSchools } from '../../core/school/schoolApi';
import { loadSingleSchool, resetSchoolCreated } from '../../core/school/schoolSlice';
import { schoolTypeOptions } from '../../core/utils/helpers';
import { createSchool } from '../../core/school/schoolApi';

import './schoolsListing.scss';

const SchoolsListing = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [filters, setFilters] = useState({
        type: [] as string[],
    });
    const [createForm] = Form.useForm();

    const {
        schools,
        isFetching,
        singleSchool,
        isSchoolCreated,
    } = useSelector((state:CombinedStates) => state.school);

    // Trigger fetch on component mount
    useEffect(() => {
        if (isEmpty(schools)) {
            dispatch(getSchools({}));
        }
        // Reset school created flag when component mounts
        dispatch(resetSchoolCreated());
    }, []);

    // Handle redirect after school creation
    useEffect(() => {
        if (isSchoolCreated && singleSchool) {
            // Navigate to edit school page
            navigate('/mokykla/redagavimas');
        }
    }, [isSchoolCreated, singleSchool, navigate]);

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];
    const navigation = [{ label: 'Item 1', link: '/abc' }, { label: 'Item 1', link: '/abc' }];

    const backLink = (
        <Button onClick={() => console.log('Back')}>
            <LeftOutlined /> BACK
        </Button>
    );

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            dispatch(getSchools({ search: value, ...filters }));
        }, 500),
        [filters],
    );

    const doSearch = (term: string) => {
        setSearchTerm(term);
        if (term.trim() === '') {
            dispatch(getSchools({ ...filters }));
        } else {
            debouncedSearch(term);
        }
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleSchool(id));
        navigate('/mokykla/redagavimas');
        // Navigate to edit form or open modal
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Ar tikrai norite ištrinti šią mokyklą?',
            okText: 'Taip, ištrinti',
            cancelText: 'Atšaukti',
            okType: 'danger',
            onOk() {
                console.log('Confirmed delete for ID:', id);
                // Čia iškviesk dispatch su delete thunk
                // dispatch(deleteSchool(id));
            },
        });
        // Show confirmation dialog and dispatch delete action
    };

    const handleCreateSchool = () => {
        setIsCreateModalVisible(true);
    };

    const handleCreateSubmit = async (values: { name: string; type: string }) => {
        try {
            await dispatch(createSchool(values)).unwrap();
            setIsCreateModalVisible(false);
            createForm.resetFields();
        } catch (error) {
            // Error is already handled in the API
            console.error('Failed to create school:', error);
        }
    };

    const handleCreateCancel = () => {
        setIsCreateModalVisible(false);
        createForm.resetFields();
    };

    return (
        <Main
            className="data-listing"
            title={<div>Mokyklų administravimas</div>}
            headerTitle={<div>Mokyklų administravimas</div>}
            menuWidth={350}
            menuPlacement="left"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent={<div />}
            floatingFooter
            backLink={false}
            breadcrumb={null}>
            <Card style={{ marginTop: '20px' }}>
                <Row className="listing-table-title" justify="space-between" align="middle">
                    <Col>
                        <div className="listing-table-title-left">
                            <h3 style={{ marginBottom: 0 }}>Mokyklos</h3>
                            <span style={{ fontWeight: 500 }}>
                                Iš viso: {schools.length}
                            </span>
                        </div>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<FilterOutlined />} onClick={() => setIsFilterVisible(true)}>Filtras</Button>
                            <Input
                                placeholder="Ieškoti pagal pavadinimą..."
                                value={searchTerm}
                                onChange={(e) => doSearch(e.target.value)}
                                allowClear />
                            <Tooltip title="Atnaujinti sąrašą">
                                <Button icon={<ReloadOutlined />}
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilters({ type: [] });
                                        dispatch(getSchools({}));
                                    }} />
                            </Tooltip>
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateSchool}>Sukurti mokyklą</Button>
                        </Space>
                    </Col>
                </Row>
                <Table
                    dataSource={schools}
                    loading={isFetching}
                    scroll={{ x: 'max-content' }}
                    rowKey="id"
                    key="schools-table"
                    columns={[
                        {
                            title: 'Id',
                            dataIndex: 'id',
                            key: 'id',
                        },
                        {
                            title: 'Pavadinimas',
                            dataIndex: 'name',
                            key: 'name',
                        },
                        {
                            title: 'Tipas',
                            dataIndex: 'type',
                            key: 'type',
                            render: (value: string) => {
                                const unit = schoolTypeOptions.find((opt) => opt.value === value);
                                return unit ? <Tag color={unit.color}>{unit.label}</Tag> : value;
                            },
                        },
                        {
                            title: 'Adresas',
                            dataIndex: 'address',
                            key: 'address',
                        },
                        {
                            title: 'Telefonas',
                            dataIndex: 'phone',
                            key: 'phone',
                        },
                        {
                            title: 'Veiksmai',
                            key: 'actions',
                            render: (_text, record) => (
                                <Space>
                                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record.id)}>Redaguoti</Button>
                                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)}>Pašalinti</Button>
                                </Space>
                            ),
                        },
                    ]} />
            </Card>
            <Modal
                title="Filtruoti mokyklas"
                visible={isFilterVisible}
                onCancel={() => setIsFilterVisible(false)}
                onOk={() => {
                    dispatch(getSchools({ ...filters, search: searchTerm }));
                    setIsFilterVisible(false);
                }}
                footer={[
                    <Button key="reset"
                        onClick={() => {
                            setFilters({ type: [] });
                            dispatch(getSchools({ search: searchTerm }));
                            setIsFilterVisible(false);
                        }}>
                        Išvalyti filtrus
                    </Button>,
                    <Button key="submit"
                        type="primary"
                        onClick={() => {
                            dispatch(getSchools({ ...filters, search: searchTerm }));
                            setIsFilterVisible(false);
                        }}>
                        Filtruoti
                    </Button>,
                ]}>
                <Form layout="vertical">
                    <Form.Item label="Tipas">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.type}
                            onChange={(value) => setFilters({ ...filters, type: value })}>
                            {schoolTypeOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Sukurti naują mokyklą"
                visible={isCreateModalVisible}
                onCancel={handleCreateCancel}
                footer={null}>
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreateSubmit}>
                    <Form.Item
                        label="Mokyklos pavadinimas"
                        name="name"
                        rules={[{ required: true, message: 'Įveskite mokyklos pavadinimą.' }]}>
                        <Input placeholder="Įveskite mokyklos pavadinimą" />
                    </Form.Item>

                    <Form.Item
                        label="Mokyklos tipas"
                        name="type"
                        rules={[{ required: true, message: 'Pasirinkite mokyklos tipą.' }]}>
                        <Select
                            placeholder="Pasirinkite mokyklos tipą"
                            options={schoolTypeOptions} />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={isFetching}>
                                Sukurti mokyklą
                            </Button>
                            <Button onClick={handleCreateCancel}>
                                Atšaukti
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Main>
    );
};

export default SchoolsListing;

SchoolsListing.propTypes = {};
